// frontend/src/lib/analytics.js
// Fire-and-forget event tracking: web vs app detection, offline queue,
// batch flush, optional GA4 forwarding. Kabhi bhi UI crash NAHI karega.
import { api, StorageKeys } from "./api";
import { storage, getDeviceId } from "./storage";

const QUEUE_KEY = "gs_analytics_queue";
const MAX_QUEUE = 100;
const FLUSH_MS = 12000;

/* ---------- session key (per app/browser session) ---------- */
let sessionKey = "s_unknown";
try {
  sessionKey = window.sessionStorage?.getItem("gs_session_key") || "";
  if (!sessionKey) {
    sessionKey = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.sessionStorage?.setItem("gs_session_key", sessionKey);
  }
} catch {}

/* ---------- WEB vs APP (APK/WebView) vs PWA detection ---------- */
export function detectPlatform() {
  try {
    const ua = navigator.userAgent || "";
    if (window.GurSetuApp || window.ReactNativeWebView) return "app";
    if (/;\s*wv\)/.test(ua)) return "app";          // Android WebView wrapper (web-to-APK)
    if (/\bGurSetu\b/i.test(ua)) return "app";      // custom UA set by wrapper (if any)
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      navigator.standalone === true;
    if (standalone) return "pwa";
    return "web";
  } catch {
    return "web";
  }
}

/* ---------- offline-safe queue ---------- */
function readQueue() {
  try {
    const raw = storage.get(QUEUE_KEY, []);
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}
function writeQueue(q) {
  try {
    storage.set(QUEUE_KEY, q.slice(-MAX_QUEUE));
  } catch {}
}

let flushTimer = null;
let flushing = false;

export async function flushQueue() {
  if (flushing) return;
  const q = readQueue();
  if (!q.length) return;
  flushing = true;
  try {
    await api.post("/analytics/batch", { events: q });
    writeQueue([]);
  } catch {
    // offline / server down → queue retained, retry later
  } finally {
    flushing = false;
  }
}

function scheduleFlush(delay = FLUSH_MS) {
  if (typeof window === "undefined") return;
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, delay);
}

/* ---------- pagehide / background flush (keepalive) ---------- */
function beaconFlush() {
  try {
    const q = readQueue();
    if (!q.length) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    fetch(api.url("/analytics/batch"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events: q }),
      keepalive: true,
    }).catch(() => {});
    writeQueue([]);
  } catch {}
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", beaconFlush);
  window.addEventListener("online", () => scheduleFlush(800));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") beaconFlush();
    else scheduleFlush(1500);
  });
}

/* ---------- meaningful engagement signal (for feedback popup) ---------- */
const MEANINGFUL_EVENTS = new Set([
  "hukamnama_open", "hukamnama_audio_start", "hukamnama_scan_open", "hukamnama_understand_open",
  "hukamnama_sahayak_open", "nitnem_open", "nitnem_bani_open", "nitnem_audio_start",
  "simran_start", "simran_session_complete", "mool_mantar_start", "ai_message_send",
  "journal_save", "feature_action",
]);
let _engagedThisSession = 0;

function signalEngagement(event) {
  if (!MEANINGFUL_EVENTS.has(event)) return;
  _engagedThisSession += 1;
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("gs:engagement", { detail: { count: _engagedThisSession, event } }));
    }
    // mark that this session had an engaged action (for "prior engaged session" rule)
    window.sessionStorage?.setItem("gs_session_engaged", "1");
    storage.set("gs_had_engaged_session", true);
  } catch {}
}

export function getSessionEngagedCount() {
  return _engagedThisSession;
}

/* ---------- PUBLIC: track(event, props) ---------- */
export function track(event, props = {}) {
  try {
    if (!event) return;
    const entry = {
      event: String(event).slice(0, 80),
      props: props && typeof props === "object" ? props : {},
      deviceId: getDeviceId(),
      userId: storage.get(StorageKeys.UserId, "") || null,
      platform: detectPlatform(),
      lang: storage.get(StorageKeys.Lang, "en") || "en",
      path: typeof window !== "undefined" ? window.location.pathname : "",
      sessionKey,
    };
    const q = readQueue();
    q.push(entry);
    writeQueue(q);

    signalEngagement(entry.event);

    // Optional: Google Analytics 4 forward (only if gtag loaded in index.html)
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      try {
        window.gtag("event", entry.event, { ...entry.props, platform: entry.platform });
      } catch {}
    }
    scheduleFlush(q.length >= 10 ? 800 : FLUSH_MS);
  } catch {}
}

export function trackPageView(path) {
  track("page_view", { path: path || "" });
}

export const trackFeatureOpen = (feature, props = {}) => track("feature_open", { feature, ...props });
export const trackFeatureAction = (feature, action, props = {}) => track("feature_action", { feature, action, ...props });
export const trackPopupImpression = (popupId, props = {}) => track("popup_impression", { popupId, ...props });
export const trackPopupAction = (popupId, action, props = {}) => track("popup_action", { popupId, action, ...props });

let _appOpened = false;
export function initAnalytics() {
  if (_appOpened || typeof window === "undefined") return;
  _appOpened = true;
  try {
    getDeviceId();
    track("app_open", { referrer: document.referrer || "", platform: detectPlatform() });
  } catch {}
}